import React, { Fragment, useCallback, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemText,
} from "@material-ui/core";
import { mergeDeepRight, not, remove } from "ramda";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import { isBlank, mapIndexed, notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import { makeStyles } from "@material-ui/core/styles";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import EditIcon from "@material-ui/icons/Edit";
import Grid from "@material-ui/core/Grid";
import LabeledReadOnly from "../../components/LabeledReadOnly";
import ReadOnlyField from "../../components/ReadOnlyField";
import YesNoCard from "../../components/YesNoCard";
import CustomDropdownDialog from "../edit/CustomDropdownDialog";
import ConfirmButton from "../../components/ConfirmButton";

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

const CustomDropdownCard = ({ project, loading, onUpdate }) => {
  const classes = useStyles();

  const [dialogOpen, setDialogOpen] = useState(false);
  const handleAddClick = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleDialogApply = useCallback(
    async data => {
      //call parent callback to save updated data.
      setDialogOpen(false);
      console.log(data);
      const update = mergeDeepRight(project, {
        fieldOptions: {
          custom_dropdown: {
            fieldLabel: data.customDropdownLabel,
            required: data.customDropdownRequired,
          },
        },
        customDropdownItems: data.customDropdownItems,
      });
      console.log(update);
      onUpdate(update);
    },
    [project, onUpdate],
  );

  const handleDeleteClick = useCallback(
    async idx => {
      const update = mergeDeepRight(project, {
        customDropdownItems: remove(idx, 1, project.customDropdownItems),
      });

      console.log(update);
      onUpdate(update);
    },
    [project, onUpdate],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          color: "primary",
          variant: "h6",
        }}
        title="Custom Dropdown"
        action={
          <Tooltip title="Edit Custom Dropdown">
            <IconButton onClick={handleAddClick}>
              <EditIcon color="primary" fontSize="large" />
            </IconButton>
          </Tooltip>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {loading && <PanelLoadingIndicator />}
        {isBlank(project) && (
          <Box padding={5} fontSize="1.4rem" fontStyle="italic">
            Error Loading Custom Dropdown.
          </Box>
        )}

        {notBlank(project) && (
          <Grid container spacing={3}>
            <Grid item sm={9}>
              <LabeledReadOnly label="Custom Dropdown Label">
                <ReadOnlyField>
                  {project.fieldOptions["custom_dropdown"]
                    ? project.fieldOptions["custom_dropdown"].fieldLabel
                    : null}
                </ReadOnlyField>
              </LabeledReadOnly>
            </Grid>

            <Grid item sm={3}>
              <LabeledReadOnly label="Required">
                <YesNoCard
                  value={
                    project.fieldOptions["custom_dropdown"] &&
                    project.fieldOptions["custom_dropdown"].required
                  }
                />
              </LabeledReadOnly>
            </Grid>

            <Grid item sm={12}>
              <LabeledReadOnly label="Options">
                <Fragment>
                  {isBlank(project.customDropdownItems) && (
                    <Box padding={5} fontSize="1.4rem" fontStyle="italic">
                      No Custom Dropdown items are set up for this Project.
                    </Box>
                  )}
                  {notBlank(project.customDropdownItems) && (
                    <List dense style={{ maxHeight: 150, overflowY: "auto" }}>
                      {mapIndexed(
                        (cd, idx) => (
                          <ListItem
                            key={cd.customDropdownId || cd.customDropdownName}
                          >
                            <ListItemText
                              primary={cd.customDropdownName}
                              secondary={not(cd.active) ? "INACTIVE" : null}
                              secondaryTypographyProps={{
                                color: "error",
                                variant: "subtitle2",
                              }}
                            />
                            <ListItemSecondaryAction>
                              {isBlank(cd.customDropdownId) && (
                                <ConfirmButton
                                  handleDelete={() => handleDeleteClick(idx)}
                                  dialogTitle="Remove Custom Dropdown"
                                  dialogBody="Are you sure you want to remove this custom dropdown?"
                                />
                              )}
                            </ListItemSecondaryAction>
                          </ListItem>
                        ),
                        project.customDropdownItems,
                      )}
                    </List>
                  )}
                </Fragment>
              </LabeledReadOnly>
            </Grid>
          </Grid>
        )}
      </CardContent>

      {dialogOpen && (
        <CustomDropdownDialog
          open={dialogOpen}
          project={project}
          fieldOptions={project.fieldOptions}
          onCancel={handleCancel}
          onApply={handleDialogApply}
        />
      )}
    </Card>
  );
};

export default CustomDropdownCard;
